import { createContext, useCallback, useContext, useEffect, useState } from 'react'

const DarkModeContext = createContext()
const SHARED_THEME_KEY = 'loadboard:theme'
const LEGACY_DARK_MODE_KEY = 'darkMode'
const THEME_EVENT = 'loadline:themechange'

const isValidTheme = value => value === 'dark' || value === 'light'

const getInitialTheme = () => {
	try {
		const sharedTheme = localStorage.getItem(SHARED_THEME_KEY)
		if (isValidTheme(sharedTheme)) return sharedTheme

		const legacyMode = localStorage.getItem(LEGACY_DARK_MODE_KEY)
		if (legacyMode != null) return JSON.parse(legacyMode) ? 'dark' : 'light'
	} catch {
		// localStorage can fail in private mode; default to light.
	}
	return 'light'
}

const syncHtmlBackground = theme => {
	document.documentElement.style.backgroundColor = theme === 'dark' ? '#1c2127' : '#ffffff'
}

const persistTheme = theme => {
	try {
		localStorage.setItem(SHARED_THEME_KEY, theme)
		localStorage.setItem(LEGACY_DARK_MODE_KEY, JSON.stringify(theme === 'dark'))
	} catch {
		// localStorage can fail in private mode; keep the in-memory theme.
	}
}

const broadcastThemeChange = theme => {
	try {
		window.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: theme }))
	} catch {
		// CustomEvent can fail in older browsers; storage still carries state.
	}
}

export const useDarkMode = () => {
	return useContext(DarkModeContext)
}

export const DarkModeProvider = ({ children }) => {
	const [theme, setTheme] = useState(getInitialTheme)
	const isDarkMode = theme === 'dark'

	const setSharedTheme = useCallback(next => {
		setTheme(next)
		persistTheme(next)
		broadcastThemeChange(next)
	}, [])

	const toggleDarkMode = () => {
		setSharedTheme(isDarkMode ? 'light' : 'dark')
	}

	useEffect(() => {
		syncHtmlBackground(theme)
	}, [theme])

	useEffect(() => {
		const applyTheme = next => {
			if (isValidTheme(next)) setTheme(next)
		}

		const onThemeEvent = event => {
			applyTheme(event?.detail)
		}

		const onStorage = event => {
			if (event.key === SHARED_THEME_KEY) {
				applyTheme(event.newValue)
				return
			}
			if (event.key === LEGACY_DARK_MODE_KEY && event.newValue != null) {
				try {
					applyTheme(JSON.parse(event.newValue) ? 'dark' : 'light')
				} catch {
					// Ignore malformed legacy values.
				}
			}
		}

		window.addEventListener(THEME_EVENT, onThemeEvent)
		window.addEventListener('storage', onStorage)
		return () => {
			window.removeEventListener(THEME_EVENT, onThemeEvent)
			window.removeEventListener('storage', onStorage)
		}
	}, [])

	return (
		<DarkModeContext.Provider value={{ isDarkMode, toggleDarkMode }}>
			{children}
		</DarkModeContext.Provider>
	)
}
